import React, { useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { HiOutlineXMark, HiOutlineVariable, HiOutlinePencilSquare, HiOutlineTrash } from 'react-icons/hi2';
import { SLOT_DATA_TYPE_CAPTURE_BY_AI } from '../../action-journey-studio/common/const/slot-data-type';
import Button from '../../../components/button/button';
import { journeyGet, journeySlotSave } from '../../../services/model/journey-service';

const TYPES = [
    ['any', 'Any value'], [SLOT_DATA_TYPE_CAPTURE_BY_AI, 'Custom (capture with AI)'], ['number', 'Number'], ['boolean', 'Yes / no'],
    ['email', 'Email'], ['date', 'Date'], ['datetime', 'Date and time'], ['document', 'Document'],
];

export default function VariableDialog({ appId, journeyId, slots = {}, startOnCreate, onSaved, onClose }) {
    const [creating, setCreating] = useState(startOnCreate || Object.keys(slots).length === 0);
    const [editingName, setEditingName] = useState(null);
    const [deletingName, setDeletingName] = useState(null);
    const [currentSlots, setCurrentSlots] = useState(slots);
    const busy = useRef(false);
    const [name, setName] = useState('');
    const [dataType, setDataType] = useState('any');
    const [content, setContent] = useState('');
    const nameRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const close = () => { if (!saving) onClose(); };

    const beginEdit = (key = null) => {
        const variable = key ? currentSlots[key] : null;
        setEditingName(key);
        setName(key || '');
        setDataType(variable?.dataType || 'any');
        setContent(variable?.content || '');
        setError('');
        setCreating(true);
    };
    const cancelForm = () => {
        if (busy.current) return;
        if (startOnCreate) onClose();
        else { setCreating(false); setEditingName(null); setError(''); }
    };
    const saveSlots = async updated => {
        const { status } = await journeySlotSave(appId, journeyId, { slots: updated });
        if (status !== 200) throw new Error('Could not save variables. Please try again.');
        setCurrentSlots(updated);
        onSaved(updated);
    };
    const removeVariable = async () => {
        if (busy.current) return;
        busy.current = true;
        setSaving(true);
        setError('');
        try {
            const journey = await journeyGet(appId, journeyId);
            if (!journey?.journeyId) throw new Error('Could not load variables. Please try again.');
            const updated = { ...journey.slots };
            delete updated[deletingName];
            await saveSlots(updated);
            setDeletingName(null);
        } catch (e) { setError(e.message || 'Could not delete the variable. Please try again.'); }
        finally { busy.current = false; setSaving(false); }
    };

    const submit = async event => {
        event.preventDefault();
        if (busy.current) return;
        const slot = name.trim();
        if (!editingName && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(slot)) {
            setError('Start with a letter or underscore. Use only letters, numbers and underscores.');
            return;
        }
        if (dataType === SLOT_DATA_TYPE_CAPTURE_BY_AI && !content.trim()) {
            setError('Describe what the agent should capture for this variable.');
            return;
        }
        busy.current = true;
        setSaving(true);
        setError('');
        try {
            // Fetch immediately before saving: the endpoint replaces the entire slot map.
            const journey = await journeyGet(appId, journeyId);
            if (!journey?.journeyId) throw new Error('The playbook is still loading. Please try again.');
            const existing = journey.slots || {};
            if (!editingName && Object.prototype.hasOwnProperty.call(existing, slot)) {
                throw new Error('A variable with this name already exists.');
            }
            if (editingName && !Object.prototype.hasOwnProperty.call(existing, editingName)) {
                throw new Error('This variable no longer exists. Cancel and add it again.');
            }
            const variable = editingName ? { ...existing[editingName] } : {
                slot, displayText: slot, captureFromUserInput: true, dependencies: [],
            };
            variable.dataType = dataType;
            if ([SLOT_DATA_TYPE_CAPTURE_BY_AI, 'regex', 'values'].includes(dataType)) variable.content = content.trim();
            else delete variable.content;
            const updated = { ...existing, [slot]: variable };
            await saveSlots(updated);
            if (startOnCreate || !editingName) onClose();
            else { setCreating(false); setEditingName(null); }

        } catch (e) {
            setError(e.message || 'Could not save the variable. Please try again.');
        } finally {
            busy.current = false;
            setSaving(false);
        }
    };

    return (
        <Dialog open initialFocus={creating && !editingName ? nameRef : undefined} onClose={close} className="relative z-50">
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />
            <div className="fixed inset-0 overflow-y-auto p-4">
                <div className="flex min-h-full items-center justify-center">
                    <Dialog.Panel className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
                        <div className="mb-5 flex items-start justify-between">
                            <div>
                                <HiOutlineVariable className="mb-3 rounded-lg bg-gray-100 p-2 text-primary" size={38} />
                                <Dialog.Title className="text-lg font-semibold text-gray-900">{creating ? editingName ? 'Edit variable' : 'Add variable' : 'Variables'}</Dialog.Title>
                                <Dialog.Description className="mt-1 text-sm text-gray-500">Save information your agent can use later.</Dialog.Description>
                            </div>
                            <button type="button" aria-label="Close variables" disabled={saving} onClick={close} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100"><HiOutlineXMark size={20} /></button>
                        </div>
                        {creating ? (
                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <label htmlFor="playbook-variable-name" className="mb-2 block text-sm font-medium text-gray-900">Name</label>
                                    <input ref={nameRef} type="text" name="slot" autoComplete="off" spellCheck={false} aria-describedby="playbook-variable-name-help" id="playbook-variable-name" value={name} onChange={e => setName(e.target.value)} placeholder="order_id" disabled={saving || !!editingName} className="block w-full rounded-xl border border-gray-300 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:text-gray-500" />
                                    <p id="playbook-variable-name-help" className="mt-2 text-xs text-gray-500">{editingName ? 'The name stays the same so existing steps keep their references.' : 'Use a short name, like order_id or customer_email.'}</p>
                                </div>
                                <div>
                                    <label htmlFor="playbook-variable-type" className="mb-2 block text-sm font-medium text-gray-900">Value type</label>
                                    <select id="playbook-variable-type" value={dataType} disabled={saving} onChange={e => setDataType(e.target.value)} className="block w-full rounded-xl border border-gray-300 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary">
                                        {!TYPES.some(([value]) => value === dataType) && <option value={dataType}>{dataType}</option>}
                                        {TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                    </select>
                                </div>
                                {[SLOT_DATA_TYPE_CAPTURE_BY_AI, 'regex', 'values'].includes(dataType) && (
                                    <div>
                                        <label htmlFor="playbook-variable-description" className="mb-2 block text-sm font-medium text-gray-900">{dataType === 'regex' ? 'Regular expression' : dataType === 'values' ? 'Allowed values (comma-separated)' : 'Description'}</label>
                                        <textarea id="playbook-variable-description" name="content" rows={3} value={content}
                                            onChange={e => setContent(e.target.value)} disabled={saving}
                                            placeholder="For example, the customer's order reference from their message."
                                            className="block w-full rounded-xl border border-gray-300 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" />
                                        <p className="mt-2 text-xs text-gray-500">{dataType === SLOT_DATA_TYPE_CAPTURE_BY_AI ? 'Tell the agent what information to capture.' : 'Define the values this variable accepts.'}</p>
                                    </div>
                                )}
                                {error && <p role="alert" className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700">{error}</p>}
                                <div className="flex justify-end gap-2 pt-2">
                                    <Button type="button" color="default" text="Cancel" disabled={saving} onClick={cancelForm} />
                                    <Button type="submit" text={editingName ? "Save changes" : "Add variable"} loading={saving} disabled={saving} />
                                </div>
                            </form>
                        ) : deletingName ? (
                            <div className="space-y-4">
                                <h3 className="font-medium text-gray-900">Delete variable?</h3>
                                <p className="break-words text-sm text-gray-500">Delete “{deletingName}”? Steps that use this variable will keep their references.</p>
                                {error && <p role="alert" className="text-sm text-rose-600">{error}</p>}
                                <div className="flex justify-end gap-2">
                                    <Button text="Cancel" color="default" disabled={saving} onClick={() => { setDeletingName(null); setError(''); }} />
                                    <Button text="Delete variable" color="error" disabled={saving} loading={saving} onClick={removeVariable} />
                                </div>
                            </div>
                        ) : (
                            <>
                                <ul className="mb-5 max-h-64 divide-y divide-gray-100 overflow-y-auto">
                                    {Object.entries(currentSlots).map(([key, slot]) => (
                                        <li key={key} className="flex items-center justify-between gap-3 py-3 text-sm">
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate font-medium text-gray-800">{key}</p>
                                                <p className="mt-1 text-xs text-gray-500">{TYPES.find(([value]) => value === slot.dataType)?.[1] || slot.dataType}</p>
                                            </div>
                                            <button type="button" aria-label={`Edit ${key}`} title="Edit variable" onClick={() => beginEdit(key)} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 focus:ring-2 focus:ring-primary/20"><HiOutlinePencilSquare size={18} /></button>
                                            <button type="button" aria-label={`Delete ${key}`} title="Delete variable" onClick={() => { setDeletingName(key); setError(''); }} className="rounded-lg p-2 text-gray-500 hover:bg-rose-50 hover:text-rose-600 focus:ring-2 focus:ring-rose-200"><HiOutlineTrash size={18} /></button>
                                        </li>
                                    ))}
                                </ul>
                                {Object.keys(currentSlots).length === 0 && <p className="mb-5 text-sm text-gray-500">No variables yet.</p>}
                                <div className="flex justify-end border-t border-gray-100 pt-4">
                                    <Button text="Add variable" onClick={() => beginEdit()} />
                                </div>
                            </>
                        )}
                    </Dialog.Panel>
                </div>
            </div>
        </Dialog>
    );
}
